<script>
  import { Icon, XMark, User, Sparkles, ExclamationTriangle } from "svelte-hero-icons";
  import { ai } from "../store";
  import LoadingDots from "./LoadingDots.svelte";

  let messagesContainer;
  let shouldAutoScroll = true;
  let previousConversationLength = 0;

  $: conversation = ai.conversation;
  $: isLoading = ai.isLoading;
  $: error = ai.error;

  // Auto-scroll to bottom only when new messages arrive and user was at bottom
  $: if ($conversation.length > previousConversationLength && shouldAutoScroll) {
    setTimeout(() => {
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }, 100);
    previousConversationLength = $conversation.length;
  } else if ($conversation.length !== previousConversationLength) {
    previousConversationLength = $conversation.length;
  }

  function handleScroll() {
    if (messagesContainer) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
      // Check if user is near the bottom (within 50px)
      shouldAutoScroll = scrollTop + clientHeight >= scrollHeight - 50;
    }
  }

  function formatTimestamp(timestamp) {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  function handleClose() {
    ai.closeSidebar();
  }

  // Detect if user is on Mac for keyboard shortcut display
  let isMac = false;
  if (typeof navigator !== "undefined") {
    isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
  }
</script>

<div class="w-80 shrink-0 bg-surface shadow-xl border-l border-line-strong flex flex-col h-full">
  <!-- Header -->
  <div class="flex items-center justify-between p-4 border-b border-line-strong bg-surface-sunken">
    <div class="flex items-center space-x-2">
      <Icon src={Sparkles} class="w-5 h-5 text-brand" />
      <h3 class="font-semibold text-content">AI Assistant</h3>
    </div>
    <button
      on:click={handleClose}
      class="text-content-subtle hover:text-content-muted transition-colors"
      title="Close AI Assistant"
    >
      <Icon src={XMark} class="w-5 h-5" />
    </button>
  </div>

  <!-- Messages container -->
  <div
    bind:this={messagesContainer}
    on:scroll={handleScroll}
    class="flex-1 overflow-y-auto p-4 space-y-4"
  >
    {#if $conversation.length === 0}
      <div class="flex flex-col items-center justify-center h-full text-content-subtle">
        <Icon src={Sparkles} class="w-12 h-12 mb-2 text-gray-300" />
        <p class="text-sm text-center">Your AI conversation will appear here</p>
      </div>
    {:else}
      {#each $conversation as message (message.id)}
        <div class="flex space-x-3">
          <!-- Avatar -->
          <div class="flex-shrink-0">
            {#if message.type === "user"}
              <div class="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center">
                <Icon src={User} class="w-4 h-4 text-white" />
              </div>
            {:else if message.type === "assistant"}
              <div class="w-8 h-8 bg-brand rounded-full flex items-center justify-center">
                <Icon src={Sparkles} class="w-4 h-4 text-white" />
              </div>
            {:else if message.type === "error"}
              <div class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                <Icon src={ExclamationTriangle} class="w-4 h-4 text-white" />
              </div>
            {/if}
          </div>

          <!-- Message content -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center space-x-2 mb-1">
              <span class="text-sm font-medium text-content">
                {#if message.type === "user"}
                  You
                {:else if message.type === "assistant"}
                  AI Assistant
                {:else if message.type === "error"}
                  Error
                {/if}
              </span>
              <span class="text-xs text-content-subtle">
                {formatTimestamp(message.timestamp)}
              </span>
            </div>

            <div class="text-sm text-content whitespace-pre-wrap break-words">
              {message.content}{#if message.type === "assistant" && message.complete === false}<span
                  class="inline-block w-1.5 h-3.5 bg-brand animate-pulse ml-1 align-text-bottom"
                ></span>{/if}
            </div>
          </div>
        </div>
      {/each}
    {/if}

    <!-- Loading indicator -->
    {#if $isLoading}
      <div class="flex space-x-3">
        <div class="w-8 h-8 bg-brand rounded-full flex items-center justify-center">
          <Icon src={Sparkles} class="w-4 h-4 text-white" />
        </div>
        <div class="flex-1">
          <div class="flex items-center space-x-2 mb-1">
            <span class="text-sm font-medium text-content">AI Assistant</span>
            <span class="text-xs text-content-subtle">thinking...</span>
          </div>
          <div class="mt-4">
            <LoadingDots />
          </div>
        </div>
      </div>
    {/if}

    <!-- Error indicator -->
    {#if $error}
      <div class="bg-red-50 border border-red-200 rounded-lg p-3">
        <div class="flex items-center space-x-2">
          <Icon src={ExclamationTriangle} class="w-5 h-5 text-red-500" />
          <span class="text-sm font-medium text-red-800">Error</span>
        </div>
        <p class="text-sm text-red-700 mt-1">{$error}</p>
      </div>
    {/if}
  </div>

  <!-- Quick actions footer -->
  <div class="border-t border-brand/10 p-4 bg-brand/5">
    <button
      on:click={() => ai.openOverlay()}
      class="w-full px-4 py-2 text-sm bg-brand hover:bg-brand/90 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
    >
      <span>Ask another question</span>
      <kbd
        class="px-1.5 py-0.5 text-xs bg-white/20 text-white rounded border border-white/30 font-mono"
      >
        {isMac ? "⌘ K" : "Ctrl + K"}
      </kbd>
    </button>
  </div>
</div>
